import { useMutation } from '@apollo/client'
import get from 'lodash/get'
import { useSelector } from 'react-redux'

import {
  Attachment,
  CreateInvoicePaymentRequestDocument,
  OrderPartialPaymentRequestsDocument,
  OrderRequestStatusDocument,
  SendInvoicesPaymentRequestDocument,
} from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { getAccountId, getCurrentUser } from 'store/currentUser/selectors'

atlas`
  mutation SendInvoicesPaymentRequest($input: SendInvoicesForOrdersInput!) {
    sendInvoicesForOrders(input: $input) {
      orderId
      sentTo
      sentOn
    }
  }
`

atlas`
  mutation CreateInvoicePaymentRequest($input: CreateInvoicesInput!) {
    createInvoices(input: $input) {
      orderId
      invoiceId
    }
  }
`

type SendInvoicesMutationParams = {
  orderId?: string
  webOrderId?: string
  retailerId?: string
  brandId?: string
  poNumber?: string
  bccEmails?: string[]
  sentToEmail?: string
  billingAddress?: string
  retailerName?: string
  retailerEmail?: string
  customSubject?: string
  customMessage?: string
  brandContactName?: string
  attachments?: Attachment[]
  amount?: string
  currency?: string
  isPartialPaymentRequest?: boolean
  name?: string
  dueDate?: string
}

type CreateInvoiceParams = {
  orderId?: string
  retailerId?: string
  isPartialPaymentRequest?: boolean
  amount?: string
  currency?: string
  name?: string
  dueDate?: string
}

const getRefetchPaymentInfoQueries = (orderId?: string) => {
  if (!orderId) {
    return undefined
  }

  return [
    {
      query: OrderPartialPaymentRequestsDocument,
      variables: {
        orderId,
      },
    },
    {
      query: OrderRequestStatusDocument,
      variables: {
        filters: {
          ids: [orderId as string],
        },
      },
    },
  ]
}

export function useSendInvoice(orderId?: string) {
  const accountId = useSelector(getAccountId)
  const currentUser = useSelector(getCurrentUser)

  const [mutate] = useMutation(SendInvoicesPaymentRequestDocument, {
    client: atlasClient,
    refetchQueries: getRefetchPaymentInfoQueries(orderId),
  })
  return {
    sendInvoice: ({
      orderId = '',
      webOrderId = '',
      retailerId,
      brandId,
      poNumber = '',
      bccEmails,
      sentToEmail,
      billingAddress,
      retailerName,
      retailerEmail,
      customSubject,
      customMessage,
      brandContactName,
      attachments,
      isPartialPaymentRequest = false,
      amount,
      currency,
      name,
      dueDate,
    }: SendInvoicesMutationParams) => {
      return mutate({
        variables: {
          input: {
            accountId: toGlobalId('Account', accountId) ?? '',
            invoicesParameters: [
              {
                orderId,
                webOrderId,
                destinationAccountId:
                  toGlobalId('Account', fromGlobalId(retailerId).id) ?? '',
                orderBrandId:
                  toGlobalId('Account', fromGlobalId(brandId).id) ?? '',
                poNumber: poNumber,
                brandName: get(currentUser, 'account.profileName'),
                brandUsername: currentUser.name || currentUser.email,
                sentFrom: currentUser.email,
                sentBcc: bccEmails,
                sentTo: sentToEmail,
                language: currentUser.emailLanguage,
                ...(billingAddress && { billingAddress }),
                ...(retailerName && { retailerName }),
                ...(retailerEmail && { retailerEmail }),
                ...(customSubject && { customSubject }),
                ...(customMessage && { customMessage }),
                ...(brandContactName && { brandContactName }),
                ...(attachments && { attachments }),
                ...(amount && { amount }),
                ...(currency && { currency }),
                ...(isPartialPaymentRequest && {
                  isPartialPaymentRequest: isPartialPaymentRequest,
                }),
                ...(name && { name }),
                ...(dueDate && { dueDate }),
              },
            ],
          },
        },
      })
    },
  }
}

export function useCreateInvoice(orderId?: string) {
  const [mutate] = useMutation(CreateInvoicePaymentRequestDocument, {
    client: atlasClient,
    refetchQueries: getRefetchPaymentInfoQueries(orderId),
  })
  return {
    createInvoice: ({
      orderId = '',
      retailerId = '',
      isPartialPaymentRequest = false,
      amount,
      currency,
      name,
      dueDate,
    }: CreateInvoiceParams) => {
      return mutate({
        variables: {
          input: {
            orderIds: [orderId],
            retailerId,
            amount,
            currency,
            ...(isPartialPaymentRequest && {
              isPartialPaymentRequest: isPartialPaymentRequest,
            }),
            ...(name && { name }),
            ...(dueDate && { dueDate }),
          },
        },
      })
    },
  }
}
